import { memo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const ANIMATIONS = {
  background: { duration: 1.5 },
  content: { duration: 1, delay: 0.5 },
  description: { duration: 1, delay: 1 },
};

function Hero({ heroImage }) {
  const { t } = useTranslation();
  const heroData = t("components.solution.hero", { returnObjects: true });

  return (
    <div
      className="relative h-[85vh] landscape:max-lg:h-[125vh] w-full overflow-hidden bg-black"
      role="banner"
      aria-label="Solution hero section"
    >
      <motion.div
        initial={{ scale: 1.2 }}
        animate={{ scale: 1 }}
        transition={{ duration: ANIMATIONS.background.duration }}
        className="absolute inset-0 overflow-hidden"
      >
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-scroll brightness-60"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-linear-to-br from-black via-gray-900/80 to-blue-900 opacity-85" />
      </motion.div>

      <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: ANIMATIONS.content.duration,
            delay: ANIMATIONS.content.delay,
          }}
          className="text-center space-y-8"
        >
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-gray-100 tracking-tight">
            <span className="block bg-linear-to-r from-blue-400 via-blue-300 to-green-300 text-transparent bg-clip-text mb-4">
              {heroData.heading}
            </span>
            <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light block text-blue-400">
              {heroData.subheading}
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: ANIMATIONS.description.duration,
              delay: ANIMATIONS.description.delay,
            }}
            className="text-md sm:text-lg md:text-xl lg:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            {heroData.description}
          </motion.p>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-24 bg-linear-to-t from-gray-900 via-gray-800 to-transparent" />
    </div>
  );
}

Hero.propTypes = {
  heroImage: PropTypes.string.isRequired,
};

export default memo(Hero);
